import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import type { PlayerStatRow } from "../../../shared/player-stats";

/** The clipped overlap keeps the inset panel from showing through the
 * translucent portrait above it. Shared by player and spirit award panels. */
export const STATS_SUBCARD =
  "relative z-0 mx-3 -mt-5 rounded-b-2xl border border-input bg-card px-3 pt-9 pb-4 [clip-path:inset(1.25rem_0_0_0)] sm:mx-4 sm:px-5 dark:border-border dark:bg-muted";

/** Recorded totals only: callers omit this panel when no stat row exists. */
export function PlayerStatsPanel({
  name,
  stats,
  compact = false,
}: {
  name: string;
  stats: Pick<PlayerStatRow, "goals" | "assists" | "total">;
  compact?: boolean;
}) {
  const t = useT();
  return (
    <dl
      aria-label={t("board.award.stats", { name })}
      className={cn(
        STATS_SUBCARD,
        "grid grid-cols-3 divide-x divide-input dark:divide-border",
        compact && "pt-7 pb-2",
      )}
    >
      {[
        { label: t("board.award.goals"), value: stats.goals },
        { label: t("board.award.assists"), value: stats.assists },
        { label: t("board.sort.total"), value: stats.total },
      ].map(({ label, value }) => (
        <div
          key={label}
          className={cn(
            "flex min-w-0 flex-col items-center px-2 text-center",
            compact ? "gap-0.5" : "gap-1",
          )}
        >
          <dt
            className={cn(
              "order-2 text-muted-foreground",
              compact ? "text-[11px] leading-tight" : "text-xs",
            )}
          >
            {label}
          </dt>
          <dd
            className={cn(
              "nxt tabular",
              compact ? "text-lg" : "text-xl sm:text-2xl",
              "leading-none",
            )}
          >
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
